import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Loading, StatCard } from "@/components/PageUI";
import { CreditCard, DollarSign, Mail, Sparkles, Trash2, Users } from "lucide-react";

const PLANS = ["Free", "Starter", "Growth", "Scale", "Enterprise"];
const ROLES = ["brand", "admin"];

export default function Admin() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("users");
  const metrics = useQuery({ queryKey: ["admin-metrics"], queryFn: () => api.get("/admin/metrics").then((r) => r.data) });
  const users = useQuery({ queryKey: ["admin-users"], queryFn: () => api.get("/admin/users").then((r) => r.data), enabled: tab === "users" });
  const payments = useQuery({ queryKey: ["admin-payments"], queryFn: () => api.get("/admin/payments").then((r) => r.data), enabled: tab === "payments" });
  const leads = useQuery({ queryKey: ["admin-leads"], queryFn: () => api.get("/admin/leads").then((r) => r.data), enabled: tab === "leads" });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-metrics"] }); };
  const update = async (id, patch) => { try { await api.patch(`/admin/users/${id}`, patch); toast.success("User updated"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return; try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } };

  const m = metrics.data || {};
  return (
    <>
      <PageHeader eyebrow="Platform administration" title="The whole network, at a glance." description="Manage users and plans, track revenue, and review inbound leads." />
      <section className="stats-grid">
        <StatCard label="Total users" value={m.total_users ?? "—"} icon={Users} />
        <StatCard label="Active campaigns" value={m.active_campaigns ?? "—"} icon={Sparkles} tone="green" />
        <StatCard label="Revenue" value={`$${(m.revenue || 0).toLocaleString()}`} icon={DollarSign} tone="amber" />
        <StatCard label="Contact leads" value={m.leads ?? "—"} icon={Mail} tone="pink" />
      </section>

      <div className="adm-tabs" data-testid="admin-tabs">
        <button className={tab === "users" ? "on" : ""} onClick={() => setTab("users")} data-testid="admin-tab-users"><Users size={15} /> Users</button>
        <button className={tab === "payments" ? "on" : ""} onClick={() => setTab("payments")} data-testid="admin-tab-payments"><CreditCard size={15} /> Payments</button>
        <button className={tab === "leads" ? "on" : ""} onClick={() => setTab("leads")} data-testid="admin-tab-leads"><Mail size={15} /> Leads</button>
      </div>

      {tab === "users" && (users.isLoading ? <Loading /> : (
        <div className="adm-table users" data-testid="admin-users-table">
          <div className="adm-head"><span>User</span><span>Company</span><span>Role</span><span>Plan</span><span>Joined</span><span>Del</span></div>
          {(users.data || []).map((u) => (
            <div className="adm-row" key={u.id} data-testid={`admin-user-${u.id}`}>
              <div className="adm-user"><span className="adm-ava">{u.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</span><div><b>{u.name}</b><small>{u.email}</small></div></div>
              <span className="adm-muted">{u.company}</span>
              <select value={u.role} onChange={(e) => update(u.id, { role: e.target.value })} data-testid={`admin-role-${u.id}`}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
              <select value={u.plan} onChange={(e) => update(u.id, { plan: e.target.value })} data-testid={`admin-plan-${u.id}`}>{PLANS.map((p) => <option key={p} value={p}>{p}</option>)}</select>
              <span className="adm-muted">{new Date(u.created_at).toLocaleDateString()}</span>
              <button className="adm-del" onClick={() => remove(u)} data-testid={`admin-delete-${u.id}`} aria-label="Delete user"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      ))}

      {tab === "payments" && (payments.isLoading ? <Loading /> : (
        <>
          <section className="stats-grid three">
            <StatCard label="Total revenue" value={`$${(payments.data?.summary.total_revenue || 0).toLocaleString()}`} icon={DollarSign} />
            <StatCard label="Paid" value={payments.data?.summary.paid_count ?? 0} icon={CreditCard} tone="green" />
            <StatCard label="Pending" value={payments.data?.summary.pending_count ?? 0} icon={CreditCard} tone="amber" />
          </section>
          <div className="adm-table pay" data-testid="admin-payments-table">
            <div className="adm-head"><span>Date</span><span>Plan</span><span>Amount</span><span>Status</span><span>Session</span></div>
            {(payments.data?.transactions || []).length ? payments.data.transactions.map((t) => (
              <div className="adm-row" key={t.session_id}>
                <span className="adm-muted">{new Date(t.created_at).toLocaleString()}</span>
                <span>{t.plan}</span><span>${(t.amount || 0).toLocaleString()}</span>
                <span className={`adm-badge ${t.payment_status}`}>{t.payment_status}</span>
                <span className="adm-muted mono">{(t.session_id || "").slice(0, 16)}…</span>
              </div>
            )) : <div className="adm-empty" data-testid="admin-payments-empty">No transactions yet. Upgrades will appear here.</div>}
          </div>
        </>
      ))}

      {tab === "leads" && (leads.isLoading ? <Loading /> : (
        <div className="adm-table leads" data-testid="admin-leads-table">
          <div className="adm-head"><span>Date</span><span>Name</span><span>Company</span><span>Team</span><span>Message</span></div>
          {(leads.data || []).length ? leads.data.map((l) => (
            <div className="adm-row" key={l.id} data-testid={`admin-lead-${l.id}`}>
              <span className="adm-muted">{new Date(l.created_at).toLocaleDateString()}</span>
              <div><b>{l.name}</b><small>{l.email}</small></div>
              <span className="adm-muted">{l.company}</span><span className="adm-muted">{l.team_size}</span>
              <span className="adm-msg" title={l.message}>{l.message}</span>
            </div>
          )) : <div className="adm-empty" data-testid="admin-leads-empty">No leads yet. Contact-form submissions will appear here.</div>}
        </div>
      ))}
    </>
  );
}
